import mockConfig from './mock.json';

const DEFAULT_REQUEST_TIMEOUT_MS = 10000;
const HTTP_URL_PATTERN = /^https?:\/\/[^/\s?#]+(?:[/?#][^\s]*)?$/i;
const encodeMatchConfigUrl = url => encodeURI(url).replace(/%25([0-9a-f]{2})/gi, '%$1');

const createMatchConfigError = (code, message, originalError) => {
  const error = new Error(message);
  error.code = code;
  if (originalError) {
    error.originalError = originalError;
  }
  return error;
};

const normalizeMatchConfigUrl = matchConfigUrl => {
  const rawUrl = typeof matchConfigUrl === 'string' ? matchConfigUrl.trim() : '';
  let normalizedUrl = '';

  try {
    normalizedUrl = rawUrl ? encodeMatchConfigUrl(rawUrl) : '';
  } catch (error) {
    throw createMatchConfigError('INVALID_MATCH_CONFIG_URL', '对阵配置地址必须是有效的 HTTP(S) URL', error);
  }

  if (!normalizedUrl || !HTTP_URL_PATTERN.test(normalizedUrl)) {
    throw createMatchConfigError('INVALID_MATCH_CONFIG_URL', '对阵配置地址必须是有效的 HTTP(S) URL');
  }
  return normalizedUrl;
};

const normalizeRequestTimeout = requestTimeoutMs => {
  const normalizedTimeout = Number(requestTimeoutMs);
  return isFinite(normalizedTimeout) && normalizedTimeout > 0 ? normalizedTimeout : DEFAULT_REQUEST_TIMEOUT_MS;
};

const isJsonResponse = response => {
  if (!response.headers || typeof response.headers.get !== 'function') {
    return true;
  }
  const contentType = response.headers.get('content-type') || '';
  return !contentType || contentType.toLowerCase().indexOf('json') > -1;
};

const parseJsonResponse = response => {
  if (typeof response.json !== 'function' || !isJsonResponse(response)) {
    return Promise.reject(createMatchConfigError('MATCH_CONFIG_RESPONSE_NOT_JSON', '对阵配置响应不是 JSON'));
  }

  return response.json().catch(error => {
    throw createMatchConfigError('MATCH_CONFIG_RESPONSE_NOT_JSON', '对阵配置响应不是有效 JSON', error);
  });
};

const validateMatchConfigEnvelope = data => {
  if (Object.prototype.toString.call(data) !== '[object Object]' || Object.keys(data).length === 0) {
    throw createMatchConfigError('MATCH_CONFIG_EMPTY_RESPONSE', '对阵配置响应必须是非空对象');
  }
  return data;
};

const requestMatchConfig = (matchConfigUrl, signal) => {
  const requestOptions = signal ? { signal } : {};

  return fetch(matchConfigUrl, requestOptions)
    .then(response => {
      if (!response || !response.ok) {
        const status = response && typeof response.status !== 'undefined' ? response.status : 0;
        throw createMatchConfigError('MATCH_CONFIG_HTTP_ERROR', `对阵配置请求失败（HTTP ${status}）`);
      }
      return parseJsonResponse(response);
    })
    .then(validateMatchConfigEnvelope)
    .catch(error => {
      if (error && error.code === 'MATCH_CONFIG_REQUEST_ABORTED') {
        throw error;
      }
      if (error && error.name === 'AbortError') {
        throw createMatchConfigError('MATCH_CONFIG_REQUEST_ABORTED', '对阵配置请求已取消', error);
      }
      if (error && error.code) {
        throw error;
      }
      throw createMatchConfigError('MATCH_CONFIG_REQUEST_FAILED', '对阵配置请求失败', error);
    });
};

const createAbortContext = externalSignal => {
  const AbortControllerClass = typeof window !== 'undefined' ? window.AbortController : undefined;
  const controller = AbortControllerClass ? new AbortControllerClass() : null;
  let removeExternalListener = () => {};

  if (controller && externalSignal && typeof externalSignal.addEventListener === 'function') {
    const abortFromExternal = () => controller.abort();
    if (externalSignal.aborted) {
      abortFromExternal();
    } else {
      externalSignal.addEventListener('abort', abortFromExternal);
      removeExternalListener = () => externalSignal.removeEventListener('abort', abortFromExternal);
    }
  }

  return {
    controller,
    signal: controller ? controller.signal : externalSignal,
    cleanup: removeExternalListener
  };
};

const withRequestTimeout = (requestPromise, requestTimeoutMs, abortContext) => {
  let timeoutId;
  const timeoutPromise = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      if (abortContext.controller) {
        abortContext.controller.abort();
      }
      reject(createMatchConfigError('MATCH_CONFIG_REQUEST_TIMEOUT', `对阵配置请求超时（${requestTimeoutMs}ms）`));
    }, requestTimeoutMs);
  });

  const complete = value => {
    clearTimeout(timeoutId);
    abortContext.cleanup();
    return value;
  };
  const completeWithError = error => {
    clearTimeout(timeoutId);
    abortContext.cleanup();
    throw error;
  };

  return Promise.race([requestPromise, timeoutPromise]).then(complete, completeWithError);
};

/**
 * 异步读取地址接口的命名 mock 场景。
 * @param {{scenario?: string}} [options]
 * @returns {Promise<{matchConfigUrl: string}>}
 */
export function getMatchConfigUrl(options = {}) {
  return Promise.resolve().then(() => {
    const scenarioName = options.scenario || mockConfig.defaultScenario;
    const scenario = mockConfig.scenarios[scenarioName];

    if (!scenario) {
      throw createMatchConfigError('UNKNOWN_MATCH_CONFIG_SCENARIO', `未知的对阵配置地址场景：${scenarioName}`);
    }
    if (scenario.outcome === 'reject') {
      const mockError = scenario.error || {};
      throw createMatchConfigError(mockError.code || 'MATCH_CONFIG_URL_REQUEST_FAILED', mockError.message || '对阵配置地址请求失败');
    }
    if (scenario.outcome !== 'resolve') {
      throw createMatchConfigError('INVALID_MATCH_CONFIG_SCENARIO', `无效的对阵配置地址场景：${scenarioName}`);
    }

    const matchConfigUrl = scenario.response && scenario.response.matchConfigUrl;
    if (!matchConfigUrl) {
      throw createMatchConfigError('MATCH_CONFIG_URL_MISSING', '对阵配置地址响应缺少 matchConfigUrl');
    }

    return { matchConfigUrl: normalizeMatchConfigUrl(matchConfigUrl) };
  });
}

/**
 * 请求线上对阵 JSON，只负责传输层和响应根节点校验。
 * @param {string} matchConfigUrl
 * @param {number} [requestTimeoutMs=10000]
 * @param {AbortSignal} [externalSignal]
 * @returns {Promise<Object>}
 */
export function fetchMatchConfig(matchConfigUrl, requestTimeoutMs = DEFAULT_REQUEST_TIMEOUT_MS, externalSignal) {
  return Promise.resolve().then(() => {
    const normalizedUrl = normalizeMatchConfigUrl(matchConfigUrl);
    const normalizedTimeout = normalizeRequestTimeout(requestTimeoutMs);
    if (externalSignal && externalSignal.aborted) {
      throw createMatchConfigError('MATCH_CONFIG_REQUEST_ABORTED', '对阵配置请求已取消');
    }

    const abortContext = createAbortContext(externalSignal);
    const requestPromise = requestMatchConfig(normalizedUrl, abortContext.signal);
    return withRequestTimeout(requestPromise, normalizedTimeout, abortContext);
  });
}
